import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useI18n, useSelector } from '~Hooks';
import takeAwayIcon from '../../Assets/Icons/shopping-bag.svg';
import eatHereIcon from '../../Assets/Icons/eat-here.svg';
import { getSmallLogo } from '../../themes/getTheme';
import { SERVICE_TYPE } from '~../__queries__';

const logoLandscapeRatio = 0.76;
export const navbarHeight = 200;
export const landscapeNavbarHeight = 140;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: ${navbarHeight}px;
  background: ${({ theme }) => theme.brandColor1};
  padding: 0 36px;
  position: relative;

  @media (orientation: landscape) {
    height: ${landscapeNavbarHeight}px;
  }
`;
const Icon = styled.div<{
  icon: any;
  width?: number;
  height?: number;
  margin?: number;
}>`
  width: ${({ width }) => (width ? `${width}px` : '32px')};
  height: ${({ height }) => (height ? `${height}px` : 'auto')};
  mask-image: url(${({ icon }) => icon});
  mask-size: contain;
  mask-repeat: no-repeat;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  background-color: ${({ theme }) => theme.complimentaryColor1};
  margin-left: ${({ margin }) => margin || 0}px;

  @media (orientation: landscape) {
    width: ${({ width }) => (width ? `${Math.round(width * logoLandscapeRatio)}px` : '32px')};
    height: ${({ height }) => (height ? `${Math.round(height * logoLandscapeRatio)}px` : 'auto')};
    margin-left: 6px;
  }
`;

const ColoredIcon = styled.img<{
  width?: number;
  height?: number;
  margin?: number;
}>`
  width: ${({ width }) => (width ? `${width}px` : '32px')};
  height: ${({ height }) => (height ? `${height}px` : 'auto')};
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  margin-left: ${({ margin }) => margin || 0}px;

  @media (orientation: landscape) {
    width: ${({ width }) => (width ? `${Math.round(width * logoLandscapeRatio)}px` : '32px')};
    height: ${({ height }) => (height ? `${Math.round(height * logoLandscapeRatio)}px` : 'auto')};
  }
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const Actions = styled.div`
  align-self: center;
`;
const Button = styled.div<{ outline?: boolean; pressed?: boolean }>`
  color: ${({ theme }) => theme.complimentaryColor1};
  font-family: Eina01-SemiBold;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 20px;
  text-align: center;
  border-radius: 24px;
  border: 2px solid ${({ outline, theme }) => (outline ? theme.complimentaryColor1 : 'inherit')};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 25px;
  background: ${({ pressed }) => (pressed ? 'rgba(255, 255, 255, 0.15)' : 'transparent')};
  transform: perspective(500px) translateZ(${({ pressed }) => (pressed ? -15 : 0)}px);
  transition: background 0.3s, transform 100ms;

  @media (orientation: landscape) {
    padding: 14px 23px;
  }
`;

interface Props {
  serviceType: SERVICE_TYPE | null;
  openWelcomeScreen: () => void;
}
const Navbar = ({ serviceType, openWelcomeScreen }: Props) => {
  const { i18n } = useI18n();
  const [pressed, setPressed] = useState(false);
  const companyId = useSelector((state) => state.company);

  const parseServiceType = (type: SERVICE_TYPE | null): string => {
    switch (type) {
      case 'TAKE_AWAY':
        return i18n('ServiceType.TakeAway');
      case 'EAT_HERE':
        return i18n('ServiceType.EatHere');
      default:
        return type || '';
    }
  };

  const handleClick = () => {
    setTimeout(() => {
      openWelcomeScreen();
    }, 300);
  };

  const logo = getSmallLogo(companyId);

  const getIcon = () => {
    switch (serviceType) {
      case null:
        return null;
      case 'TAKE_AWAY':
        return takeAwayIcon;
      case 'EAT_HERE':
        return eatHereIcon;
      default:
        return null;
    }
  };

  const icon = getIcon();

  return (
    <Wrapper>
      {logo.colored ? (
        <ColoredIcon src={logo.url} width={logo.width} height={logo.height} />
      ) : (
        <Logo>
          <Icon icon={logo.url} width={logo.width} height={logo.height} />
        </Logo>
      )}
      <Actions>
        <Button>
          {parseServiceType(serviceType)} <Icon width={32} height={32} icon={icon} margin={16} />
        </Button>
        <Button
          outline
          onTouchStart={() => setPressed(true)}
          onTouchEnd={() => setPressed(false)}
          onClick={handleClick}
          pressed={pressed}>
          {i18n('Express.StartOver')}
        </Button>
      </Actions>
    </Wrapper>
  );
};

export default React.memo(Navbar);
